import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './InfosStep.styles';
import {
  IconAiStar,
  IconInfo, TextField, Tooltip,
} from 'gatorcomponents';
import { GradientButton } from '@/pages/Studio/components';
import {
  isBRServer, isMXServer, validateEmail, validatePhoneBR, validatePhone,
} from '@/utils/Validators/validation';
import { COUNTRY as CONFIG_COUNTRY } from '@/config';
import { BRANDS_LIST } from '../../AiBuilderCreateSite.enum';

const InfosStep = ({
  continueButtonClick,
  infosStepLocales,
  userCountry,
  description,
  setDescription,
  telephoneInputValue,
  setTelephoneInputValue,
  emailInputValue,
  setEmailValue,
  mobile,
  iconMaia,
}) => {
  const [error, setError] = useState({
    email: '',
    telephone: '',
  });

  const disabledButton = !description || error.email !== '' || error.telephone !== '';

  const getPhoneCountry = useCallback(() => {
    if (BRANDS_LIST.includes(userCountry.toLowerCase())) {
      return userCountry.toLowerCase();
    }

    return CONFIG_COUNTRY;
  }, [userCountry]);

  const phoneCountry = getPhoneCountry();

  const isValidPhone = useCallback(() => {
    if (isBRServer) {
      return validatePhoneBR(telephoneInputValue);
    }

    if (isMXServer) {
      if (BRANDS_LIST.includes(userCountry.toLowerCase())) {
        return validatePhone(telephoneInputValue, userCountry.toLowerCase());
      }
      return telephoneInputValue.length > 6;
    }

    return validatePhone(telephoneInputValue);
  }, [telephoneInputValue, userCountry]);

  const validateErrors = useCallback(() => {
    const newErrors = { email: '', telephone: '' };

    if (emailInputValue.length > 0 && validateEmail(emailInputValue) !== true) {
      newErrors.email = infosStepLocales.errors.emailError;
    }

    if (telephoneInputValue.length > 0 && !isValidPhone()) {
      newErrors.telephone = infosStepLocales.errors.telephoneError;
    }

    setError(newErrors);
  }, [
    telephoneInputValue,
    emailInputValue,
    infosStepLocales.errors,
    isValidPhone,
  ]);

  const handleValidateErrors = useCallback(() => {
    validateErrors();
  }, [validateErrors]);

  useEffect(() => {
    handleValidateErrors();
  }, [handleValidateErrors]);

  const handleDescriptionChange = (e) => {
    setDescription && setDescription(e.target.value);
  };

  const handleTelephoneChange = (e) => {
    setTelephoneInputValue && setTelephoneInputValue(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmailValue && setEmailValue(e.target.value);
  };

  const buttonIcon = (
    <Styles.ButtonIconContainer>
      <IconAiStar
        color={disabledButton ? 'lowLight' : 'highPure'}
        size="md"
      />
    </Styles.ButtonIconContainer>
  );

  return (
    <Styles.Wrapper data-testid="infos-step-wrapper">
      <Styles.Title>
        <Styles.IconMaia src={iconMaia} />
        {infosStepLocales.title}
      </Styles.Title>
      <Styles.Subtitle>
        {infosStepLocales.subtitle}

        <Tooltip content={infosStepLocales.subtitleTooltip} position="top" width={280} allowDynamicFit>
          <IconInfo color="lowDark" size="sm" />
        </Tooltip>
      </Styles.Subtitle>

      <TextField
        testId="infos-step-description-field"
        type="textarea"
        label={infosStepLocales.textAreaLabel}
        inputHeight={98}
        onChange={handleDescriptionChange}
        value={description}
        maxLength={300}
      />

      <Styles.ContactInfosTitle>
        {infosStepLocales.contactInfos}
        <Styles.SmallText>{infosStepLocales.optional}</Styles.SmallText>
      </Styles.ContactInfosTitle>
      <Styles.SampleText>{infosStepLocales.contactInfosDescription}</Styles.SampleText>

      <Styles.IputsListContainer>
        <Styles.InputContainerPhone>
          <TextField
            testId="infos-step-telephone-field"
            label={infosStepLocales.telephonePlaceholder}
            mask="phone"
            country={phoneCountry}
            value={telephoneInputValue}
            onChange={handleTelephoneChange}
            status={error.telephone !== '' ? 'attention' : 'normal'}
            helpText={error.telephone !== '' && error.telephone}
          />
        </Styles.InputContainerPhone>

        <Styles.InputContainerEmail>
          <TextField
            testId="infos-step-email-field"
            label={infosStepLocales.emailPlaceholder}
            value={emailInputValue}
            onChange={handleEmailChange}
            maxLength={90}
            status={error.email !== '' ? 'attention' : 'normal'}
            helpText={error.email !== '' && error.email}
          />
        </Styles.InputContainerEmail>
      </Styles.IputsListContainer>

      <Styles.ButtonWrapper>
        <Tooltip content={error} allowDynamicFit disabled={!error.length}>
          <GradientButton
            testId="infos-step-continue-button"
            label={infosStepLocales.buttonLabel}
            variant="quinary"
            iconRight={buttonIcon}
            disabled={disabledButton}
            width={mobile ? 'full' : 'fit'}
            onClick={continueButtonClick}
          />
        </Tooltip>
      </Styles.ButtonWrapper>
    </Styles.Wrapper>
  );
};

export default InfosStep;
